/**
 * Linear-style circular progress icon.
 *
 * @param {number} progress  - 0 to 100
 * @param {number} size      - icon diameter in px (default 16)
 * @param {number} thickness - stroke / wedge weight as a fraction of radius (default 1 = full pie)
 * @param {string} color     - accent color (default Linear's purple)
 * @param {string} trackColor- background ring color
 * @param {number} gap       - px gap between outer ring and inner fill (default size/16)
 */
export function CircleProgress({
  progress = 0,
  size = 16,
  color = '#5e6ad2',
  trackColor = 'currentColor',
  gap = null,
  strokeWidth = null,
  max = 100,
}) {
  const clamped = Math.max(0, Math.min(max, progress))

  // Geometry — viewBox is a fixed 100x100 so everything scales cleanly.
  const center = 50
  const ring = strokeWidth ?? 8 // outer ring thickness in viewBox units
  const ringRadius = center - ring / 2 // keep the ring stroke fully inside
  const padding = gap ?? 14 // space between ring and inner pie
  const pieRadius = center - ring - padding

  // The inner pie is drawn as a single thick "stroke arc" — Linear's trick.
  // We use a circle whose stroke radius is pieRadius/2 and stroke width = pieRadius,
  // so the stroke fills the whole inner disc. dasharray then reveals a fraction.
  const fillRadius = pieRadius / 2
  const circumference = 2 * Math.PI * fillRadius
  const dash = (clamped / max) * circumference

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      fill="none"
      role="img"
      aria-label={`Progress: ${Math.round(clamped)}%`}
      style={{display: 'inline-block', verticalAlign: 'middle'}}
    >
      {/* Outer track ring */}
      <circle cx={center} cy={center} r={ringRadius} stroke={trackColor} strokeWidth={ring} opacity={0.25} />
      {/* Outer accent ring (full, subtle — gives the "container" look) */}
      <circle
        cx={center}
        cy={center}
        r={ringRadius}
        stroke={color}
        strokeWidth={ring}
        opacity={clamped > 0 ? 0.9 : 0.25}
      />
      {/* Inner pie fill */}
      {clamped > 0 && (
        <circle
          cx={center}
          cy={center}
          r={fillRadius}
          stroke={color}
          strokeWidth={pieRadius}
          strokeDasharray={`${dash} ${circumference}`}
          // start at 12 o'clock and sweep clockwise
          transform={`rotate(-90 ${center} ${center})`}
          style={{transition: 'stroke-dasharray 0.35s ease'}}
        />
      )}
    </svg>
  )
}
